import React, { useState, useMemo, useCallback } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CommonFileUploader from '../../../fileupload/CommonFileUploader/CommonFileUploader';
import {
  setSelectedSectionData,
  updateAttachments,
  updateGeneralData,
  updateTimesheet
} from '../../../../actions/timesheet';
import { tneAxiosClient } from '../../../../api/apiSettings';
import { responsiveSizeTimesheet, timesheetsStatuses } from '../../../../utils/constants';
import { useWindowSize } from '../../../../utils/hooks';
import MainButton from '../../../common/buttons/MainButton';
import BaseSwipeableDrawer from '../../../common/BaseSwipeableDrawer/BaseSwipeableDrawer';
import { basicErrorHandler } from '../../../../utils/helpers';
import CustomDocViewer from '../../../common/CustomDocViewer/CustomDocViewer';
import colors from '../../../../scss/colors';
import classes from './Attachments.module.scss';
import { buttonVariants } from '../../../common/buttons/types';
import { setNotification } from '../../../../actions/appNotifications';

export function AttachmentItem({ label, deleteHandler, deletePending, clickHandler }) {
  return (
    <div className={classes.attachItem}>
      <p role="presentation" onClick={clickHandler} className={deletePending ? classes.deleting : ''}>
        {label}
      </p>
      {!deletePending && <CloseIcon onClick={deleteHandler} sx={{ color: colors.ICON_GREY, fontSize: '16px' }} />}
    </div>
  );
}

function AttachmentsInfo({ attachments, clickHandler, open, setOpen }) {
  const { t } = useTranslation();
  const size = useWindowSize();
  // const [open, setOpen] = useState(false);

  return (
    <>
      {/* <div
        role="presentation"
        className={classes.infoContainer}
        onClick={() => {
          if (attachments.length) setOpen(true);
        }}
      >
        {size.width < responsiveSizeTimesheet && <AddAttachmentIcon />}
        <div>
          {t('dashboard.timesheet.attachments.attachmentstitle')}&nbsp;
          <span className={classes.number}>({attachments?.length})</span>
        </div>
        {size.width < responsiveSizeTimesheet && (
          <ArrowForwardIosIcon sx={{ opacity: attachments?.length ? 0.1 : 0 }} />
        )}
      </div> */}
      {size.width > responsiveSizeTimesheet ? (
        // <Dialog
        //   open={true}
        //   onClose={() => setOpen(false)}
        //   sx={{
        //     '& .MuiPaper-root.MuiDialog-paper': {
        //       maxWidth: '100%',
        //       margin: '16px',
        //       padding: '28px 32px 68px 32px'
        //     },
        //     '& .MuiDialog-paper': {
        //       borderRadius: '20px',
        //       overflowY: 'visible',
        //       paddingBottom: '48px',
        //       backgroundColor: colors.PAGE_BACKGROUND
        //     }
        //   }}
        // >
        //   <DialogTitle
        //     sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        //   >
        //     <p className={classes.dialogHeader}>{t('dashboard.timesheet.attachments.attachmentstitle')}</p>
        //     <div role="presentation" className={classes.closeIcon} onClick={() => setOpen(false)}>
        //       <CloseIcon sx={{ color: colors.MEDIUM_GREY, fontSize: '32px' }} />
        //     </div>
        //   </DialogTitle>
        <div className={classes.attachmentsContainer}>
          {attachments.map((attach) => {
            return (
              <div role="presentation" onClick={() => clickHandler(attach)} key={attach.Id}>
                {attach.FileAttachmentName}
              </div>
            );
          })}
        </div>
      ) : (
        // </Dialog>
        <BaseSwipeableDrawer height="auto" open={open} closeHandler={() => setOpen(false)}>
          <div className={classes.swiper} />
          <p className={classes.attachmentsLabel}>{t('dashboard.timesheet.attachments.attachmentstitle')}</p>
          <div className={classes.attachmentsContainer}>
            {attachments?.map((attach) => {
              return (
                <div role="presentation" onClick={() => clickHandler(attach)} key={attach.Id}>
                  {attach.FileAttachmentName}
                </div>
              );
            })}
          </div>
          <MainButton size="small" variant="primary" rounded forceWidth="204px" action={() => setOpen(false)}>
            {t('common.general.close')}
          </MainButton>
        </BaseSwipeableDrawer>
      )}
    </>
  );
}

export default function Attachments({
  status,
  timesheetId,
  attachments,
  editeMode,
  handleChanges,
  saveHandler,
  saving
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const timesheet = useSelector((state) => state?.timesheet?.data);
  const selectedSection = useSelector((state) => state?.timesheet?.selectedSectionData);

  const canBeEdited = useMemo(
    () => ![timesheetsStatuses(t).approved.label, timesheetsStatuses(t).processed.label].includes(status),
    [status]
  );

  const [loading, setLoading] = useState(false);
  const [deletePending, setDeletePending] = useState(null);
  const [opening, setOpening] = useState(false);
  const [attachment, setAttachment] = useState({
    show: false,
    id: '',
    filename: '',
    data: null
  });

  const handleAttachmentViewer = async (file) => {
    try {
      if (file?.Id) {
        setOpening(true);
        const result = await tneAxiosClient.get(`v1/timesheet/download-attachment?timesheetAttachmentId=${file?.Id}`);
        if (result.status === 200) {
          setAttachment({
            show: true,
            id: file?.Id,
            filename: file?.FileAttachmentName,
            data: result?.data?.Data
          });
        }
        setOpening(false);
      }
    } catch (e) {
      setOpening(false);
      basicErrorHandler(dispatch, e.message);
    }
  };

  const handleDownload = useCallback(() => {
    const link = document.createElement('a');
    link.href = attachment.data;
    link.download = attachment.filename;
    link.click();
  }, [attachment.filename]);

  const uploadHandler = async (files) => {
    const data = new FormData();
    data.append('attachment', files[0]);

    try {
      setLoading(true);
      if (!timesheet?.IsSplitApprovalFlow) {
        const result = await tneAxiosClient.post(`v1/timesheet/attachment/${timesheetId}`, data, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        const newAttachmentsList = [
          ...(attachments || []),
          { FileAttachmentName: files[0].name, Id: result.data.Data.Id }
        ];

        dispatch(updateGeneralData('TimesheetAttachments', newAttachmentsList));
        // TODO This is temporary solution to improve UX little bit
        handleChanges(true);
      } else {
        const filesArray = Array.from(files);

        filesArray.forEach((file) => {
          const tempId = `local-${Date.now()}-${file.name}`;
          dispatch(
            updateAttachments(selectedSection?.Id, file, {
              Id: tempId,
              FileAttachmentName: file.name,
              isLocal: true,
              rawFile: file
            })
          );
        });

        const updateSectionData = {
          ...selectedSection,
          TimesheetAttachments: [...selectedSection.TimesheetAttachments, ...filesArray]
        };
        dispatch(setSelectedSectionData(updateSectionData));
        handleChanges(true);
      }
    } catch (e) {
      console.log(e);
      basicErrorHandler(dispatch, e);
    }
    setLoading(false);
  };

  const deleteHandler = async (file) => {
    try {
      if (file?.Id) {
        setDeletePending(file?.Id);
        await tneAxiosClient.delete(`v1/timesheet/attachment/${file?.Id}`);
        const newAttachmentsList = attachments.filter((item) => item.Id !== file?.Id);
        const updatedSectionData = {
          ...selectedSection,
          TimesheetAttachments: newAttachmentsList
        };
        dispatch(setSelectedSectionData(updatedSectionData));
        if (timesheet?.IsSplitApprovalFlow) {
          dispatch(updateTimesheet(updatedSectionData, selectedSection?.Id, true));
        } else {
          dispatch(updateGeneralData('TimesheetAttachments', newAttachmentsList));
        }
        handleChanges(true);
      } else {
        const newAttachmentsList = attachments.filter((item) => item.name !== file.name);
        const updatedSectionData = {
          ...selectedSection,
          TimesheetAttachments: newAttachmentsList
        };
        dispatch(setSelectedSectionData(updatedSectionData));

        const sheet = timesheet?.TimesheetSections?.map((section) => {
          if (section.Id !== selectedSection?.Id) return section;

          return {
            ...section,
            TimesheetAttachments: section.TimesheetAttachments?.filter((att) => att.name !== file.name)
          };
        });
        if (timesheet?.IsSplitApprovalFlow) {
          dispatch(updateGeneralData('GlobalFiles', newAttachmentsList));
          dispatch(updateTimesheet(sheet, selectedSection?.Id, true));
        } else {
          dispatch(updateGeneralData('TimesheetAttachments', newAttachmentsList));
        }
      }
      dispatch(
        setNotification({
          message: `${file.FileAttachmentName || file.name} deleted successfully`,
          duration: 2000
        })
      );
    } catch (e) {
      basicErrorHandler(dispatch, e.message);
    }
    setDeletePending(null);
  };

  return (
    <div className={`${classes.uploadSection} ${!canBeEdited ? classes.withBottomGap : ''}`}>
      {editeMode && canBeEdited ? (
        <>
          <CommonFileUploader
            uploadHandler={uploadHandler}
            isMultiple={timesheet?.IsSplitApprovalFlow || false}
            acceptedFormats={['.jpeg', '.jpg', '.png', '.pdf', '.doc', '.docx', '.csv', '.xls', 'xlsx']}
            maxFileSizeMB={5}
            allowedQtyOfAttachments={10}
            existingAttachmentsQty={attachments?.length}
            loading={loading}
          />
          {attachments?.length ? (
            <div className={classes.attachmentsList}>
              {attachments?.map((attach, index) => (
                <React.Fragment key={attach.Id}>
                  <AttachmentItem
                    clickHandler={() => handleAttachmentViewer(attach)}
                    label={`${attach.FileAttachmentName || attach.name}`}
                    deleteHandler={() => deleteHandler(attach)}
                    deletePending={attach.Id === deletePending}
                  />
                  {index !== attachments.length - 1 && width < responsiveSizeTimesheet && (
                    <div className={classes.divider}>
                      <div />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          ) : (
            ''
          )}
          {timesheet?.GlobalFiles?.length && (
            <MainButton
              size="small"
              variant={buttonVariants.PRIMARY}
              rounded
              action={saveHandler}
              forceWidth="fit-content"
              pending={saving}
            >
              Save Attachments
            </MainButton>
          )}
        </>
      ) : (
        <AttachmentsInfo attachments={attachments} clickHandler={handleAttachmentViewer} />
      )}
      {(attachment?.show || opening) && (
        <CustomDocViewer
          settings={attachment}
          handleDownload={handleDownload}
          close={() =>
            setAttachment({
              show: false,
              id: '',
              filename: '',
              data: null
            })
          }
          isLoading={opening}
        />
      )}
    </div>
  );
}

// TODO ADD size to name when backend will start to send filesize {(${(attach.size / 1024 / 1024).toFixed(1)}Mb)}
